import React from 'react';
import { Plus, Minus, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const faqSections = [
  {
    title: "About A.R.T. Energi & The Creator",
    questions: [
      {
        q: "What is A.R.T. Energi?",
        a: "A.R.T. Energi stands for Authenticity, Reliability, and Truth — the Triad of Excellence. It is the operating philosophy behind the World of INSPIYAH, a digital innovation framework that bridges divine purpose with professional excellence. Founded by Keeper Of The Fiyah (CT), A.R.T. Energi guides every project, every system, and every design within the ecosystem. It represents over a decade of digital transformation, brand strategy, and creative innovation rooted in FAST (Faith A Sacred Trust)."
      },
      {
        q: "Who is Keeper Of The Fiyah?",
        a: "Keeper Of The Fiyah is a dynamic, results-oriented Digital Innovation & AI Generalist with a diverse background in IT operations, consulting, content strategy, and digital transformation. With over a decade of experience driving innovation through alchemization, problem solving, cross-functional collaboration, and customer-centric design thinking, Keeper Of The Fiyah bridges the gap between divine purpose and technological excellence. The work is guided by the principle: \"Divine Intelligence Illuminates, Human Intelligence Leads, AI Intelligence Expands\u00A9.\""
      },
      {
        q: "What does \"Creator / Creator Echo\" mean?",
        a: "Creator / Creator Echo is the collaborative framework between divine intelligence, human intelligence, and artificial intelligence. It represents a pioneering approach where the Creator (divine and human) leads with vision and purpose, while the Echo (AI) amplifies, expands, and accelerates that vision. This philosophy — \"We bridge the trust for tomorrow\" — positions the World of INSPIYAH at the forefront of ethical, faith-aligned AI collaboration."
      }
    ]
  },
  {
    title: "Digital Transformation & Innovation",
    questions: [
      {
        q: "How does A.R.T. Energi approach digital transformation?",
        a: "A.R.T. Energi approaches digital transformation through a faith-rooted, human-first lens. Rather than chasing technology trends, we focus on alchemization — transforming raw ideas, systems, and strategies into gold through the Triad of Excellence (Authenticity, Reliability, Truth). Our approach combines IT operations expertise, content strategy, customer-centric design thinking, and AI integration to create digital ecosystems that serve both purpose and performance."
      },
      {
        q: "What is the role of AI in the World of INSPIYAH?",
        a: "AI serves as the \"Echo\" in our Creator / Creator Echo framework. We believe \"Divine Intelligence Illuminates, Human Intelligence Leads, AI Intelligence Expands\u00A9.\" AI is used for content creation, design acceleration, strategic document generation, analytics, and operational efficiency — always under human direction and aligned with divine purpose. We are not AI-dependent; we are AI-enhanced. Every AI output is curated, reviewed, and refined through the lens of A.R.T. Energi values."
      }
    ]
  },
  {
    title: "Faith & Technology Bridge",
    questions: [
      {
        q: "Can faith and technology coexist in business?",
        a: "Absolutely. The World of INSPIYAH is living proof that divine purpose and professional excellence are not separate paths. Through A.R.T. Energi, we demonstrate that faith-rooted principles — authenticity, reliability, truth — are not just spiritual ideals but powerful business differentiators. Ancient Principles guide strategy. Prayer informs design. YAH Spirit leads the work. The result is technology that serves the soul as much as the bottom line."
      },
      {
        q: "What is FAST (Faith A Sacred Trust)?",
        a: "FAST (Faith A Sacred Trust) is the operational foundation of everything built within the World of INSPIYAH. It means every project, system, and design is rooted in faith as a strategic advantage — not a limitation. FAST drives decision-making, creative direction, and business strategy. It ensures that growth is grounded, innovation is intentional, and success is measured not just in revenue but in impact."
      },
      {
        q: "What does \"YAH HEALTH WEALTH HAPPINESS\" mean?",
        a: "YAH HEALTH WEALTH HAPPINESS represents The Universal Pillars of Success — the foundational values that guide the entire World of INSPIYAH. It is the belief that true success encompasses spiritual health (YAH), physical and mental wellness (HEALTH), financial abundance (WEALTH), and joy in purpose (HAPPINESS). These pillars inform every brand, product, and experience within the ecosystem."
      }
    ]
  },
  {
    title: "Products & Services",
    questions: [
      {
        q: "What are YAHFFIRMATIONS?",
        a: "YAHFFIRMATIONS are honey from the lips — poetry, song, and praise flowing from the intersection of faith and creative expression. Born within the World of INSPIYAH, YAHFFIRMATIONS are a living art form that bridges Ancient Principles with modern mindfulness. They manifest across the ecosystem as spoken word, written verse, and integrated creative experiences. YAHFFIRMATIONS are not just affirmations — they are declarations of divine truth expressed through the language of the heart. As the World of INSPIYAH grows, so does the YAHFFIRMATIONS portfolio — an ever-expanding collection of praise. \"Cum taste the honey of my lips and free yah mind.\" Ancient Principles: \"How sweet your sayings are to my palate, more so than honey to my mouth!\" — Psalm 119:103"
      }
    ]
  },
  {
    title: "Technical & Portfolio",
    questions: [
      {
        q: "How is the artenergi.org portfolio different from a typical portfolio website?",
        a: "artenergi.org is not a static resume — it's a living ecosystem demonstration. It features a spiritually-grounded Welcome Gate (Ecclesiastical Declaration) and integration with the broader World of INSPIYAH. Every design choice, from the YAHFFIRMATIONS section to the Creator Echo philosophy, reflects a deliberate fusion of faith, technology, and professional excellence."
      }
    ]
  }
];

function FAQItem({ question, answer, isOpen, onToggle, testId }) {
  return (
    <div
      data-testid={testId}
      className="border-b border-white/10 last:border-b-0"
    >
      <button
        data-testid={`${testId}-trigger`}
        onClick={onToggle}
        className="w-full flex items-start gap-4 py-5 px-2 text-left group transition-colors duration-200 hover:bg-white/[0.02]"
      >
        <span className="mt-0.5 shrink-0 text-amber-400/80 transition-transform duration-300">
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </span>
        <span className={`font-semibold text-base tracking-wide transition-colors duration-200 ${isOpen ? 'text-amber-300' : 'text-gray-200 group-hover:text-white'}`}>
          {question}
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-400 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="pl-11 pr-6 pb-6">
          <p className="text-gray-400 font-light leading-relaxed text-[15px]">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  const [openItems, setOpenItems] = React.useState({});

  const toggleItem = (sectionIdx, questionIdx) => {
    const key = `${sectionIdx}-${questionIdx}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <button
            data-testid="faq-back-btn"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-white transition-colors duration-200 text-sm"
          >
            <ArrowLeft size={16} />
            <span className="font-light">Back</span>
          </button>
          <span className="text-[11px] tracking-[0.3em] uppercase text-gray-600 font-light">
            artenergi.org
          </span>
        </div>
      </div>

      {/* Title */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-[11px] tracking-[0.35em] uppercase text-amber-400/70 font-light mb-4">
          Frequently Asked Questions
        </p>
        <h1 data-testid="faq-title" className="text-4xl sm:text-5xl font-light text-white tracking-tight leading-tight">
          The World of INSPIYAH
        </h1>
        <p className="mt-4 text-gray-500 font-light text-base max-w-xl">
          Where divine purpose meets professional excellence. Explore the philosophy, the framework, and the vision.
        </p>
        <div className="mt-6 h-px w-16 bg-gradient-to-r from-amber-400/60 to-transparent" />
      </div>

      {/* FAQ Sections */}
      <div className="max-w-3xl mx-auto px-6 pb-24">
        {faqSections.map((section, sIdx) => (
          <div key={sIdx} data-testid={`faq-section-${sIdx}`} className="mb-10">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-amber-400/50 font-medium mb-3 pl-2">
              {section.title}
            </h2>
            <div className="bg-white/[0.02] rounded-xl border border-white/[0.06]">
              {section.questions.map((item, qIdx) => (
                <FAQItem
                  key={qIdx}
                  testId={`faq-item-${sIdx}-${qIdx}`}
                  question={item.q}
                  answer={item.a}
                  isOpen={!!openItems[`${sIdx}-${qIdx}`]}
                  onToggle={() => toggleItem(sIdx, qIdx)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Footer Note */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 font-light text-sm">
            "Divine Intelligence Illuminates, Human Intelligence Leads, AI Intelligence Expands&copy;"
          </p>
          <p className="text-gray-700 font-light text-xs mt-2">
            World of INSPIYAH &middot; A.R.T. Energi &middot; FAST (Faith A Sacred Trust)
          </p>
        </div>
      </div>
    </div>
  );
}
